import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  WEEKS,
  type AdultCharts,
  type KidCharts,
  type PaceWeek,
  type PatternStrip,
  type StripMark,
  type TalkWeek,
} from './charts.ts';
import { formatDayMonth } from '../i18n/format';
import { t, type PlainKey } from '../i18n/t';
import type { Register } from '../profile/register';

/** "21 Sep" — the week's Monday, in the learner's own locale. */
const weekLabel = (start: number) => formatDayMonth(new Date(start));

const AXIS = { fontSize: 11, fill: 'var(--color-base-content)', opacity: 0.6 };

const TOOLTIP = {
  contentStyle: {
    background: 'var(--color-base-100)',
    border: '1px solid var(--chart-grid)',
    borderRadius: 8,
    color: 'var(--color-base-content)',
    fontSize: 12,
  },
  // Values and labels in text ink, never the series colour: the colour belongs
  // to the mark beside them.
  itemStyle: { color: 'var(--color-base-content)' },
  labelStyle: { color: 'var(--color-base-content)', opacity: 0.7 },
  cursor: { fill: 'var(--chart-grid)' },
};

/**
 * One chart's frame: its title, a sentence that says what it shows, the plot,
 * and the same numbers as a table — so no reading depends on the plot alone.
 */
function Figure({
  title,
  summary,
  table,
  children,
}: {
  title: string;
  summary: string;
  table: { head: string[]; rows: (string | number)[][] };
  children: React.ReactNode;
}) {
  return (
    <figure className="flex flex-col gap-2">
      <figcaption>
        <h3 className="font-semibold">{title}</h3>
        <p className="text-base-content/70 text-sm">{summary}</p>
      </figcaption>
      {/* Time runs left to right on a chart's axis in every shipped locale,
          Arabic included, and Recharts draws no mirrored SVG (spec §4.4). */}
      <div dir="ltr">{children}</div>
      <details className="text-sm">
        <summary className="text-base-content/60 cursor-pointer">{t('charts-show-table')}</summary>
        <table className="table-sm mt-2 table">
          <thead>
            <tr>
              {table.head.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

function Practising({ perWeek, register }: { perWeek: KidCharts['perWeek']; register: Register }) {
  const total = perWeek.reduce((n, w) => n + w.conversations, 0);
  const data = perWeek.map((w) => ({ week: weekLabel(w.start), conversations: w.conversations }));
  return (
    <Figure
      title={t(register === 'kid' ? 'charts-practising-kid' : 'charts-practising')}
      summary={t('charts-practising-summary', { count: total, weeks: WEEKS })}
      table={{
        head: [t('charts-week-of'), t('charts-conversations')],
        rows: data.map((d) => [d.week, d.conversations]),
      }}
    >
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis dataKey="week" tick={AXIS} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
          <Tooltip {...TOOLTIP} />
          <Bar
            dataKey="conversations"
            name={t('charts-conversations')}
            fill="var(--chart-series-1)"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Figure>
  );
}

function Learned({ learned, register }: { learned: KidCharts['learned']; register: Register }) {
  const last = learned[learned.length - 1] ?? { words: 0, patterns: 0 };
  const wordsName = t(register === 'kid' ? 'charts-learned-words-kid' : 'charts-learned-words');
  const patternsName = t(register === 'kid' ? 'charts-learned-patterns-kid' : 'charts-learned-patterns');
  const data = learned.map((p) => ({ week: weekLabel(p.start), words: p.words, patterns: p.patterns }));
  return (
    <Figure
      title={t(register === 'kid' ? 'charts-learned-kid' : 'charts-learned')}
      summary={t(register === 'kid' ? 'charts-learned-summary-kid' : 'charts-learned-summary', {
        words: last.words,
        patterns: last.patterns,
        weeks: WEEKS,
      })}
      table={{
        head: [t('charts-week-of'), wordsName, patternsName],
        rows: data.map((d) => [d.week, d.words, d.patterns]),
      }}
    >
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis dataKey="week" tick={AXIS} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
          <Tooltip {...TOOLTIP} />
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontSize: 12 }}
            // Legend text in text ink; the line icon beside it carries the colour.
            formatter={(value) => <span style={{ color: 'var(--color-base-content)' }}>{value}</span>}
          />
          <Area
            type="monotone"
            dataKey="words"
            name={wordsName}
            stroke="var(--chart-series-1)"
            strokeWidth={2}
            fill="var(--chart-series-1)"
            fillOpacity={0.12}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="patterns"
            name={patternsName}
            stroke="var(--chart-series-2)"
            strokeWidth={2}
            fill="var(--chart-series-2)"
            fillOpacity={0.12}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Figure>
  );
}

function Talked({ talked, register }: { talked: readonly TalkWeek[]; register: Register }) {
  const total = Math.round(talked.reduce((n, w) => n + w.minutes, 0));
  const data = talked.map((w) => ({ week: weekLabel(w.start), minutes: w.minutes }));
  return (
    <Figure
      title={t(register === 'kid' ? 'charts-talked-kid' : 'charts-talked')}
      summary={t('charts-talked-summary', { count: total, weeks: WEEKS })}
      table={{ head: [t('charts-week-of'), t('charts-minutes')], rows: data.map((d) => [d.week, d.minutes]) }}
    >
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis dataKey="week" tick={AXIS} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={AXIS} tickLine={false} axisLine={false} />
          <Tooltip {...TOOLTIP} />
          <Bar
            dataKey="minutes"
            name={t('charts-minutes')}
            fill="var(--chart-series-1)"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Figure>
  );
}

/**
 * Adult only. Its own chart rather than a second axis on "time spent
 * speaking": two measures of different scale on one plot is the chart that
 * invites reading a correlation nobody measured.
 */
function Pace({ pace }: { pace: readonly PaceWeek[] }) {
  const spoken = pace.filter((w) => w.wpm !== null);
  if (spoken.length === 0) return null;
  const first = spoken[0].wpm!;
  const last = spoken[spoken.length - 1].wpm!;
  const data = pace.map((w) => ({ week: weekLabel(w.start), wpm: w.wpm }));
  return (
    <Figure
      title={t('charts-pace')}
      summary={spoken.length === 1 ? t('charts-pace-summary-one', { last }) : t('charts-pace-summary', { last, first })}
      table={{
        head: [t('charts-week-of'), t('charts-words-a-minute')],
        rows: data.map((d) => [d.week, d.wpm ?? '—']),
      }}
    >
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -24 }}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis dataKey="week" tick={AXIS} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis domain={['auto', 'auto']} allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
          <Tooltip {...TOOLTIP} />
          {/* A silent week is a gap, not a line drawn through it: joining
              across it would claim a pace nobody spoke at. */}
          <Line
            type="monotone"
            dataKey="wpm"
            name={t('charts-words-a-minute')}
            stroke="var(--chart-series-1)"
            strokeWidth={2}
            dot={{ r: 4, fill: 'var(--chart-series-1)', stroke: 'var(--color-base-100)', strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Figure>
  );
}

/** Each mark's glyph, colour and words. Shape first: colour is never alone. */
const MARKS: Record<Exclude<StripMark, null>, { glyph: string; color: string; label: PlainKey; inline: PlainKey }> = {
  got: { glyph: '●', color: 'var(--chart-series-1)', label: 'mark-got', inline: 'mark-got-inline' },
  back: { glyph: '✕', color: 'var(--chart-series-2)', label: 'mark-back', inline: 'mark-back-inline' },
  // Hollow, not a dot: a `·` was too small to see among the others.
  missed: { glyph: '○', color: 'var(--chart-neutral)', label: 'mark-missed', inline: 'mark-missed-inline' },
};

/**
 * One row per pattern, one cell per week — a grid of glyphs rather than a
 * plot, so it is already the table a screen reader reads.
 */
function Strips({ strips, weeks }: { strips: readonly PatternStrip[]; weeks: readonly number[] }) {
  if (strips.length === 0) return null;
  const summary = strips
    .map((s) => {
      const last = [...s.marks].reverse().find((m) => m !== null);
      return last ? t('charts-strips-summary-item', { pattern: s.label, mark: t(MARKS[last].inline) }) : null;
    })
    .filter(Boolean)
    .join('; ');
  return (
    <figure className="flex flex-col gap-2">
      <figcaption>
        <h3 className="font-semibold">{t('charts-strips-title')}</h3>
        <p className="text-base-content/70 text-sm">{t('charts-strips-summary', { summary })}</p>
      </figcaption>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr>
              <th className="sr-only">{t('charts-strips-pattern')}</th>
              {weeks.map((w, i) => (
                <th key={w} className="text-base-content/50 px-0 text-center text-[10px] font-normal">
                  {i === 0 || i === weeks.length - 1 ? weekLabel(w) : <span className="sr-only">{weekLabel(w)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {strips.map((s) => (
              <tr key={s.itemId}>
                <th scope="row" className="pe-3 text-start font-normal whitespace-nowrap">
                  {s.label}
                </th>
                {s.marks.map((m, i) => (
                  <td key={i} className="text-center leading-none">
                    {m === null ? (
                      <span className="sr-only">{t('charts-strips-not-practised')}</span>
                    ) : (
                      <span title={t(MARKS[m].label)} style={{ color: MARKS[m].color }} className="text-base">
                        <span aria-hidden>{MARKS[m].glyph}</span>
                        <span className="sr-only">{t(MARKS[m].label)}</span>
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="text-base-content/70 flex flex-wrap gap-4 text-xs">
        {Object.values(MARKS).map((m) => (
          <li key={m.label} className="flex items-center gap-1">
            <span aria-hidden style={{ color: m.color }}>
              {m.glyph}
            </span>
            {t(m.label)}
          </li>
        ))}
      </ul>
    </figure>
  );
}

/**
 * "Your last 12 weeks". What a register may see was decided in `charts.ts`;
 * a `KidCharts` has no strips to render.
 */
export function ProgressCharts({ charts, register }: { charts: KidCharts | AdultCharts; register: Register }) {
  return (
    <section className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold">{t('charts-title', { weeks: WEEKS })}</h2>
      <Practising perWeek={charts.perWeek} register={register} />
      <Talked talked={charts.talked} register={register} />
      {'pace' in charts && <Pace pace={charts.pace} />}
      <Learned learned={charts.learned} register={register} />
      {'strips' in charts && <Strips strips={charts.strips} weeks={charts.perWeek.map((w) => w.start)} />}
    </section>
  );
}
